import React, { forwardRef, useEffect, useState } from 'react';

import classNames from 'classnames';
import FadeCollapse from 'components/FadeCollapse/FadeCollapse';

import { Category, Permission } from 'generated/graphql';
import { useCategoryContext } from 'components/CategoryList/CategoryContext';
import { useVenueContext } from 'routes/Main';

import EditableText from 'components/Inputs/EditableText';

import styles from './DraggableCategory.module.scss';
import { InlineAddHandler } from '../Item/InlineAdd/InlineAddHandler';
import { SelectMenu } from '../../SelectMenu';
import { useModalContext } from '../../../common/Context/ModalContext';
import EditableDescription from './EditableDescription/EditableDescription';
import UncategorisedDropDown from './UncategorisedDropDown/UncategorisedDropDown';
import { extractCategoryName, extractMenuName } from '../../DoshiiMenuLogic/splitMenu';
import { getEnvironmentVariables } from '../../../services/environment-variables/environment-variables';
import { fromVirtualBrandId } from '../../../utils/brand';

export interface Props {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  hover?: boolean;
  handleProps?: React.HTMLAttributes<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  shadow?: boolean;
  categoryNames: string[];
  placeholder?: boolean;
  unstyled?: boolean;
  category: Category | undefined;
  isDragOverlay?: boolean;
}

const DraggableCategory = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      handleProps,
      hover,
      placeholder,
      style,
      categoryNames,
      shadow,
      unstyled,
      category,
      isDragOverlay,
      ...props
    }: Props,
    ref
  ) => {
    const {
      deleteCategory,
      handleToggleCategoryCollapse,
      isLoading,
      collapsedCategories,
      changedCategoryIds,
      updateCategory
    } = useCategoryContext();
    const { currentUser } = useVenueContext();
    const { openModal, closeModal } = useModalContext();
    const { NO_CATEGORY_DESCRIPTION_APP_IDS } = getEnvironmentVariables();
    const isUncategorised = category && category.mmId === 'UNCATEGORISED';
    const canViewDescription =
      isUncategorised ||
      !category?.idsByApp.some(({ app }) => NO_CATEGORY_DESCRIPTION_APP_IDS.includes(fromVirtualBrandId(app.id).appId));

    const canEditItem = !!currentUser?.permissions?.includes(Permission.PublishMenu) && !isUncategorised;
    const usesDoshiiLogic = category?.category.startsWith('doshii:menu:');
    const menuName = extractMenuName(category?.category || '');
    const [categoryName, setCategoryName] = useState(extractCategoryName(category?.category || ''));

    // Update Category name logic
    useEffect((): void => {
      if (!canEditItem || !category || categoryName === extractCategoryName(category.category)) return;
      // Revert empty names
      if (!categoryName.trim()) {
        setCategoryName(extractCategoryName(category.category));
        return;
      }
      // Revert names with doshii:menu: logic
      if (categoryName.startsWith('doshii:menu:')) {
        openModal({
          type: 'ERROR',
          canDismiss: true,
          title: 'This is not a valid category name.',
          body: 'Please choose another name.',
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          buttons: [{ label: 'OK', callback: () => {} }]
        });
        setCategoryName(extractCategoryName(category.category));
        return;
      }
      // Revert duplicate names, display warning
      if (categoryNames.some((blacklist) => extractCategoryName(blacklist) === categoryName.toLowerCase())) {
        openModal({
          type: 'ERROR',
          canDismiss: true,
          title: 'This category name has already been used.',
          body: 'Please choose another name.',
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          buttons: [{ label: 'OK', callback: () => {} }]
        });

        setCategoryName(extractCategoryName(category.category));
        return;
      }

      updateCategory({
        category: usesDoshiiLogic ? `doshii:menu:${menuName}:${categoryName}` : categoryName,
        idsByApp: category.idsByApp
      });
    }, [canEditItem, category, categoryName, categoryNames, menuName, openModal, updateCategory, usesDoshiiLogic]);

    if (!category) return null;

    const isOpen = !collapsedCategories.includes(category.mmId);
    const hasChanged = changedCategoryIds.includes(category?.mmId);

    const description =
      !isLoading && canEditItem ? (
        <EditableDescription description={category.description} idsByApp={category.idsByApp} />
      ) : (
        <p className={isLoading ? 'LoadingCoverText' : ''}>{category.description}</p>
      );
    return (
      <section
        aria-labelledby={`category-title-${categoryName}`}
        {...props}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
        style={
          {
            ...style
          } as React.CSSProperties
        }
        className={classNames(
          styles.Container,
          isUncategorised && styles.uncategorised,
          unstyled && styles.unstyled,
          hover && styles.hover,
          placeholder && styles.placeholder,
          shadow && styles.shadow,
          !isOpen ? styles.Collapsed : '',
          isDragOverlay && styles.DragOverlay
        )}
        data-testid={`category-list-category-${category.mmId}`}
        data-id={category.mmId}
      >
        <div className={styles.CategoryHeader}>
          <div className={styles.CategoryHeadingButton}>
            <h2 className={isLoading ? 'LoadingCoverText' : ''} id={`category-title-${categoryName}`}>
              {canEditItem ? (
                <EditableText
                  text={categoryName}
                  afterContent={
                    isLoading ? null : (
                      <>
                        <span>
                          {category.productIds.length} item{category.productIds.length === 1 ? '' : 's'}
                        </span>
                        {hasChanged && <i style={{ background: 'transparent' }} className="Dot mixed" />}
                      </>
                    )
                  }
                  hasInternalState
                  onSubmit={setCategoryName}
                />
              ) : (
                categoryName
              )}
            </h2>
            <button
              className={styles.CategoryCollapseButton}
              onClick={() => handleToggleCategoryCollapse(category.mmId)}
              type="button"
              aria-label={
                canEditItem ? 'Press Space to drag category, and Enter to expand' : 'Expand or collapse Category'
              }
              data-testid="category-list-category-heading-button"
              {...(canEditItem ? handleProps : {})}
            />
            <span style={{ top: 26, right: 30 }} className={styles.AngleUp}>
              <button
                aria-label="Expand category"
                onClick={() => handleToggleCategoryCollapse(category.mmId)}
                type="button"
              >
                <i className="mm-angle-up" />
              </button>
            </span>
          </div>
          {canEditItem && (
            <SelectMenu
              aria-label="Category Options"
              options={[
                {
                  content: (
                    <span className={styles.Red}>
                      <i className={`mm-trash-o ${styles.Icon}`} /> Delete Category
                    </span>
                  ),
                  isRedHoverBackground: true,
                  value: 'delete-category',
                  type: 'button',
                  callback: () => {
                    openModal({
                      type: 'ERROR',
                      title: `Delete this category and its items?`,
                      body: (
                        <span>
                          These items will be deleted from this menu. <br /> Your POS data will not be affected.
                        </span>
                      ),
                      buttons: [
                        {
                          label: 'Cancel',
                          callback: closeModal,
                          level: 'low'
                        },
                        {
                          label: 'Confirm',
                          callback: () => deleteCategory(category.mmId),
                          level: 'high'
                        }
                      ],
                      canDismiss: true
                    });
                  }
                }
              ]}
              defaultButtonContent={<i className="mm-ellipsis-v" />}
              buttonStyle={styles.CategorySelectMenuButton}
              downIcon={null}
              lineBeforeLast={false}
              align="right"
              size="small"
            />
          )}
          {isUncategorised && <UncategorisedDropDown />}
        </div>
        {!isDragOverlay && children ? (
          <FadeCollapse open={isOpen}>
            {canViewDescription && description}
            {placeholder ? children : <ul className={styles.ItemGrid}>{children}</ul>}
            {!isLoading && canEditItem && (
              <InlineAddHandler currentCategoryId={category.mmId} currentCategoryItems={category.productIds} />
            )}
          </FadeCollapse>
        ) : null}
      </section>
    );
  }
);

DraggableCategory.defaultProps = {
  children: null,
  handleProps: undefined,
  hover: false,
  isDragOverlay: false,
  placeholder: false,
  shadow: false,
  style: undefined,
  unstyled: false
};

export default DraggableCategory;
